import type {
    ClarifyingQuestion,
    CoverageState,
    EntityAnalysisResult,
    ModelContextCopilotIntent,
    ParsedMetadataEntity,
    ParsedMetadataField,
    ParsedMetadataSummary,
    StructuredContextEntity,
    StructuredContextRelationship,
    StructuredModelContext,
    SyntheticReply,
} from './modelContextTypes';
import {
    SUGGESTED_QUESTION_TEMPLATES,
    SYNTHETIC_CONSTRAINTS,
    SYNTHETIC_INTENDED_USE,
    SYNTHETIC_USAGE_GUIDELINES,
    defaultBusinessDomain,
    defaultExampleQuestions,
    defaultGlossary,
    defaultPurpose,
    packLlmInstructions,
} from './modelContextSyntheticData';

const LOG_PREFIX = '[ModelContextCopilot]';

/** Normalize schema JSON (string or object) so copilot and additionalContext always get raw metadata. */
export function formatContextNarrative(value: unknown): string {
    let text = typeof value === 'string' ? value : value == null ? '' : String(value);
    text = text.trim();
    if (!text) return '';
    if (
        (text.startsWith('"') && text.endsWith('"')) ||
        (text.startsWith("'") && text.endsWith("'"))
    ) {
        try {
            const decoded = JSON.parse(text);
            if (typeof decoded === 'string') text = decoded;
        } catch {
            /* keep original */
        }
    }
    const newlineCount = (text.match(/\n/g) || []).length;
    if (newlineCount < 2 && (text.includes('\\n') || text.includes('\\t'))) {
        text = text.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\t/g, '    ');
    }
    text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if ((text.match(/\n/g) || []).length < 2) {
        text = text.replace(/\s+-\s+(?=[A-Za-z_][\w.]*\s*:)/g, '\n- ');
        text = text.replace(/\s+(?=Columns:|Fields:|Files:|Folders:|Use this\b)/gi, '\n\n');
    }
    return text.replace(/\n{3,}/g, '\n\n').trim();
}

export function normalizeMetadataJson(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value == null) return '';
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

export function logContextCopilot(
    level: 'app' | 'warning' | 'error',
    event: string,
    extra?: Record<string, unknown>
): void {
    const payload = extra ? { event, ...extra } : { event };
    if (level === 'error') {
        console.error(LOG_PREFIX, '[error]', payload);
        return;
    }
    if (level === 'warning') {
        console.warn(LOG_PREFIX, '[warning]', payload);
        return;
    }
    console.info(LOG_PREFIX, '[app]', payload);
}

function humanizeName(value: string): string {
    return value
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function asString(value: unknown): string {
    if (typeof value === 'string') {
        return value === '[object Object]' ? '' : value;
    }
    if (value == null || typeof value === 'object') return '';
    return String(value);
}

function extractQueryText(value: unknown): string {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed || trimmed === '[object Object]') return '';
        return trimmed;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const obj = value as Record<string, unknown>;
        return extractQueryText(obj.text ?? obj.sql ?? obj.query);
    }
    return '';
}

function parseEntity(raw: Record<string, unknown>, parentId?: string): ParsedMetadataEntity {
    const id = asString(raw.id ?? raw.name) || 'entity';
    const fieldsRaw = Array.isArray(raw.fields) ? raw.fields : [];
    const fields: ParsedMetadataField[] = fieldsRaw.map((field) => {
        const f = (field && typeof field === 'object' ? field : {}) as Record<string, unknown>;
        return {
            name: asString(f.name),
            dataType: asString(f.data_type ?? f.type) || 'string',
            nullable: Boolean(f.nullable),
            isPrimaryKey: Boolean(f.is_primary_key),
            isForeignKey: Boolean(f.is_foreign_key),
        };
    });

    return {
        id,
        displayName: asString(raw.display_name) || humanizeName(id),
        entityType: asString(raw.entity_type) || 'table',
        parentId: asString(raw.parent_id) || parentId,
        queryHint: asString(raw.query_hint) || undefined,
        fields,
    };
}

function collectQueryColumns(...roots: Record<string, unknown>[]): ParsedMetadataField[] {
    const seen = new Set<string>();
    const fields: ParsedMetadataField[] = [];
    for (const root of roots) {
        const queryObj = root.query && typeof root.query === 'object' && !Array.isArray(root.query)
            ? (root.query as Record<string, unknown>)
            : undefined;
        const resultObj = root.result && typeof root.result === 'object'
            ? (root.result as Record<string, unknown>)
            : undefined;
        const columns =
            (Array.isArray(root.columns) && root.columns) ||
            (Array.isArray(root.fields) && root.fields) ||
            (queryObj && Array.isArray(queryObj.columns) && queryObj.columns) ||
            (queryObj && Array.isArray(queryObj.fields) && queryObj.fields) ||
            (resultObj && Array.isArray(resultObj.columns) && resultObj.columns) ||
            null;
        if (!Array.isArray(columns)) continue;
        for (const col of columns) {
            let name = '';
            let dataType = 'string';
            if (typeof col === 'string') {
                name = col;
            } else if (col && typeof col === 'object') {
                const c = col as Record<string, unknown>;
                name = asString(c.name ?? c.column);
                dataType = asString(c.data_type ?? c.dataType ?? c.type) || 'string';
            }
            if (!name || seen.has(name)) continue;
            seen.add(name);
            fields.push({ name, dataType });
        }
    }
    return fields;
}

export function parseMetadataSummary(
    metadataStructure: string | null,
    options?: { log?: boolean }
): ParsedMetadataSummary {
    const shouldLog = options?.log !== false;
    const empty: ParsedMetadataSummary = {
        isCustomQuery: false,
        entityCount: 0,
        fieldCount: 0,
        entities: [],
        parseOk: false,
        parseWarning: 'No metadata attached',
    };

    if (!metadataStructure?.trim()) {
        if (shouldLog) logContextCopilot('warning', 'metadata_parse_empty');
        return empty;
    }

    try {
        const parsed = JSON.parse(metadataStructure) as Record<string, unknown>;
        const root = (parsed.metadata && typeof parsed.metadata === 'object'
            ? parsed.metadata
            : parsed) as Record<string, unknown>;
        const source = (root.source && typeof root.source === 'object' ? root.source : parsed.source) as
            | Record<string, unknown>
            | undefined;
        const catalog = Array.isArray(root.catalog) ? root.catalog : [];
        const entities: ParsedMetadataEntity[] = [];

        for (const catalogEntry of catalog) {
            if (!catalogEntry || typeof catalogEntry !== 'object') continue;
            const entry = catalogEntry as Record<string, unknown>;
            const nested = Array.isArray(entry.entities) ? entry.entities : [];
            for (const entity of nested) {
                if (!entity || typeof entity !== 'object') continue;
                entities.push(parseEntity(entity as Record<string, unknown>, asString(entry.id)));
            }
        }

        const sql = extractQueryText(parsed.sql ?? parsed.query ?? root.sql ?? root.query);
        const queryColumns = collectQueryColumns(parsed, root);
        const isQueryBuilder = asString(root.type) === 'query_builder' || Boolean(sql);
        const isCustomQuery = isQueryBuilder || (entities.length === 0 && queryColumns.length > 0);

        if (entities.length === 0 && isCustomQuery) {
            entities.push({
                id: asString(parsed.id) || asString(root.id) || 'custom_query',
                displayName: asString(parsed.display_name) || asString(root.display_name) || 'Custom query',
                entityType: 'query',
                queryHint: sql || undefined,
                fields: queryColumns,
            });
        }

        const fieldCount = entities.reduce((sum, e) => sum + e.fields.length, 0);
        const summary: ParsedMetadataSummary = {
            sourceId: source ? asString(source.id) : undefined,
            sourceDisplayName: source ? asString(source.display_name) : undefined,
            sourceType: source ? asString(source.source_type) : undefined,
            category: source ? asString(source.category) : undefined,
            isCustomQuery,
            entityCount: entities.length,
            fieldCount,
            entities,
            parseOk: true,
        };

        if (shouldLog) {
            logContextCopilot('app', 'metadata_parse_ok', {
                entityCount: summary.entityCount,
                fieldCount: summary.fieldCount,
                isCustomQuery,
                metadataChars: metadataStructure.length,
            });
        }
        return summary;
    } catch (err) {
        if (shouldLog) {
            logContextCopilot('error', 'metadata_parse_failed', {
                message: err instanceof Error ? err.message : 'Invalid JSON',
                metadataChars: metadataStructure.length,
            });
        }
        return {
            ...empty,
            parseWarning: 'Metadata JSON could not be parsed; copilot will use generic defaults.',
        };
    }
}

function inferGrain(entity: ParsedMetadataEntity): string {
    const pk = entity.fields.filter((f) => f.isPrimaryKey).map((f) => f.name);
    if (entity.entityType === 'query') {
        return 'One row per result of the attached query template';
    }
    if (pk.length) {
        return `One row per ${entity.displayName.toLowerCase()} identified by ${pk.join(', ')}`;
    }
    return `One row per ${entity.displayName.toLowerCase()} record`;
}

function inferMeaning(entity: ParsedMetadataEntity): string {
    if (entity.queryHint) {
        return `Queryable ${entity.entityType} with hint: ${entity.queryHint}`;
    }
    const sample = entity.fields.slice(0, 5).map((f) => f.name).filter(Boolean);
    if (sample.length) {
        return `Stores ${entity.displayName.toLowerCase()} attributes including ${sample.join(', ')}.`;
    }
    return `Represents ${entity.displayName} in the attached catalog.`;
}

function inferRelationships(entities: ParsedMetadataEntity[]): StructuredContextRelationship[] {
    const byId = new Map(entities.map((e) => [e.id, e]));
    const rels: StructuredContextRelationship[] = [];

    for (const entity of entities) {
        for (const field of entity.fields) {
            if (!field.isForeignKey) continue;
            const target = entities.find((candidate) => {
                if (candidate.id === entity.id) return false;
                const keys = candidate.fields.filter((f) => f.isPrimaryKey).map((f) => f.name.toLowerCase());
                const fname = field.name.toLowerCase();
                return keys.includes(fname) || fname.includes(candidate.displayName.replace(/\s+/g, '').toLowerCase());
            });
            if (!target) continue;
            rels.push({
                from: entity.id,
                to: target.id,
                type: 'references',
                description: `${entity.displayName}.${field.name} references ${target.displayName}.`,
            });
        }
    }

    if (rels.length === 0 && entities.length >= 2) {
        const a = entities[0];
        const b = entities[1];
        if (a && b && byId.has(a.id)) {
            rels.push({
                from: a.id,
                to: b.id,
                type: 'related',
                description: `Treat ${a.displayName} and ${b.displayName} as related until a confirmed join key is documented.`,
            });
        }
    }

    return rels;
}

function toStructuredEntities(summary: ParsedMetadataSummary): StructuredContextEntity[] {
    return summary.entities.map((entity) => ({
        id: entity.id,
        displayName: entity.displayName,
        businessMeaning: inferMeaning(entity),
        grain: inferGrain(entity),
        keyFields: entity.fields.filter((f) => f.isPrimaryKey).map((f) => f.name).filter(Boolean),
    }));
}

export function createEmptyDraft(
    summary: ParsedMetadataSummary,
    modelName: string,
    sourceType: string,
    sourceMetadata = ''
): StructuredModelContext {
    return {
        version: '1.0',
        generatedAt: new Date().toISOString(),
        status: 'draft',
        modelName: modelName || 'Untitled model',
        sourceType: sourceType || summary.sourceType || '',
        metadataSummary: {
            entityCount: summary.entityCount,
            fieldCount: summary.fieldCount,
            entities: summary.entities.map((e) => e.id),
            isCustomQuery: summary.isCustomQuery,
        },
        purpose: '',
        businessDomain: '',
        intendedUse: [],
        entities: toStructuredEntities(summary),
        relationships: [],
        usageGuidelines: [],
        constraints: [],
        glossary: [],
        exampleQuestions: [],
        llmInstructions: '',
        selectedEntityId: undefined,
        analysisSummary: '',
        clarifyingQuestions: [],
        followUpMessages: [],
        sourceMetadata,
        sourceKind: undefined,
        usageNotes: [],
        elementDescriptions: [],
        contextNarrative: '',
    };
}

export function analyzeSelectedEntity(
    summary: ParsedMetadataSummary,
    entityId: string,
    modelName: string,
    sourceType: string
): EntityAnalysisResult {
    const entity = summary.entities.find((e) => e.id === entityId) ?? summary.entities[0];
    if (!entity) {
        logContextCopilot('warning', 'analyze_entity_missing', { entityId, entityCount: summary.entityCount });
        return {
            selectedEntityId: entityId,
            analysisSummary: 'No entity could be parsed from metadata. You can still describe how agents should use this model.',
            clarifyingQuestions: [
                { id: 'purpose', prompt: 'What should agents use this model for?', answer: '' },
                { id: 'constraints', prompt: 'What should agents never do with this data?', answer: '' },
            ],
            draftPatch: {
                selectedEntityId: entityId,
                analysisSummary: 'No entity could be parsed from metadata. You can still describe how agents should use this model.',
                clarifyingQuestions: [
                    { id: 'purpose', prompt: 'What should agents use this model for?', answer: '' },
                    { id: 'constraints', prompt: 'What should agents never do with this data?', answer: '' },
                ],
            },
        };
    }

    const grain = inferGrain(entity);
    const meaning = inferMeaning(entity);
    const pkFields = entity.fields.filter((f) => f.isPrimaryKey).map((f) => f.name).filter(Boolean);
    const fkFields = entity.fields.filter((f) => f.isForeignKey).map((f) => f.name).filter(Boolean);
    const sampleFields = entity.fields.slice(0, 8).map((f) => f.name).filter(Boolean);
    const related = inferRelationships(summary.entities).filter((r) => r.from === entity.id || r.to === entity.id);
    const structuredEntity: StructuredContextEntity = {
        id: entity.id,
        displayName: entity.displayName,
        businessMeaning: meaning,
        grain,
        keyFields: pkFields,
    };

    const analysisLines = [
        `${entity.displayName} (${entity.id}) is a ${entity.entityType || 'entity'} with ${entity.fields.length} field${entity.fields.length === 1 ? '' : 's'}.`,
        meaning,
        `Grain: ${grain}.`,
        pkFields.length ? `Primary keys: ${pkFields.join(', ')}.` : 'No primary key was marked in metadata.',
        fkFields.length ? `Foreign keys: ${fkFields.join(', ')}.` : 'No foreign keys were marked on this entity.',
        related.length
            ? `Related joins: ${related.map((r) => r.description).join(' ')}`
            : 'No confirmed joins from this entity; keep join paths conservative.',
        sampleFields.length ? `Notable columns: ${sampleFields.join(', ')}.` : '',
    ].filter(Boolean);
    const analysisSummary = analysisLines.join(' ');

    const clarifyingQuestions: ClarifyingQuestion[] = [
        {
            id: 'purpose',
            prompt: `What business questions should agents answer using ${entity.displayName}?`,
            answer: '',
        },
        related.length
            ? {
                id: 'grain',
                prompt: `Is this grain correct for ${entity.displayName}, and how should it join related tables? (${grain})`,
                answer: '',
            }
            : {
                id: 'grain',
                prompt: `Confirm the grain of ${entity.displayName}. One row represents what?`,
                answer: '',
            },
        {
            id: 'constraints',
            prompt: `What should agents never do when querying ${entity.displayName}?`,
            answer: '',
        },
    ];

    const draftPatch: Partial<StructuredModelContext> = {
        selectedEntityId: entity.id,
        analysisSummary,
        clarifyingQuestions,
        purpose: '',
        businessDomain: defaultBusinessDomain(summary, sourceType),
        intendedUse: SYNTHETIC_INTENDED_USE,
        entities: [structuredEntity],
        relationships: related,
        usageGuidelines: [],
        constraints: [],
        glossary: defaultGlossary({ ...summary, entities: [entity] }),
        exampleQuestions: [],
        llmInstructions: '',
        status: 'draft',
        modelName: modelName || 'Untitled model',
        sourceType: sourceType || summary.sourceType || '',
    };

    logContextCopilot('app', 'entity_analyzed', {
        entityId: entity.id,
        fieldCount: entity.fields.length,
        pkCount: pkFields.length,
        fkCount: fkFields.length,
        questionCount: clarifyingQuestions.length,
        modelName,
    });

    return {
        selectedEntityId: entity.id,
        analysisSummary,
        clarifyingQuestions,
        draftPatch,
    };
}

export function applyQuestionAnswers(
    draft: StructuredModelContext,
    questions: ClarifyingQuestion[]
): StructuredModelContext {
    const byId = new Map(questions.map((q) => [q.id, q.answer.trim()]));
    const purposeAnswer = byId.get('purpose') || '';
    const grainAnswer = byId.get('grain') || '';
    const constraintsAnswer = byId.get('constraints') || '';

    const entities = draft.entities.map((entity) => {
        if (draft.selectedEntityId && entity.id !== draft.selectedEntityId) return entity;
        if (!grainAnswer) return entity;
        return { ...entity, grain: grainAnswer };
    });

    const constraints = constraintsAnswer
        ? constraintsAnswer.split(/\n+/).map((line) => line.replace(/^[-•]\s*/, '').trim()).filter(Boolean)
        : draft.constraints;

    logContextCopilot('app', 'question_answers_applied', {
        answeredCount: questions.filter((q) => q.answer.trim()).length,
        questionCount: questions.length,
        selectedEntityId: draft.selectedEntityId,
    });

    return mergeDraft(draft, {
        clarifyingQuestions: questions,
        purpose: purposeAnswer || draft.purpose,
        entities,
        constraints,
    });
}

export function replyToClarifyingAnswer(
    question: ClarifyingQuestion,
    summary: ParsedMetadataSummary,
    draft: StructuredModelContext
): { assistantMessage: string; draftPatch: Partial<StructuredModelContext> } {
    const answer = question.answer.trim();
    const entity = summary.entities.find((e) => e.id === draft.selectedEntityId) ?? summary.entities[0];
    const entityName = entity?.displayName || 'this entity';
    const fieldHint = entity?.fields.slice(0, 6).map((f) => f.name).filter(Boolean).join(', ');
    const snippet = answer.length > 180 ? `${answer.slice(0, 177)}…` : answer;

    let assistantMessage = '';
    let draftPatch: Partial<StructuredModelContext> = {};

    if (!answer) {
        assistantMessage = `Add a short answer for ${entityName} and I will fold it into the tool contract.`;
        logContextCopilot('warning', 'clarify_reply_empty', { questionId: question.id });
        return { assistantMessage, draftPatch };
    }

    switch (question.id) {
        case 'purpose': {
            const examples = answer
                .split(/[\n;]+/)
                .map((line) => line.replace(/^[-•\d.)\s]+/, '').trim())
                .filter((line) => line.length > 8)
                .slice(0, 4);
            draftPatch = {
                purpose: answer,
                exampleQuestions: examples.length ? examples : draft.exampleQuestions,
            };
            assistantMessage = `Noted. Agents will treat ${entityName} as answering: ${snippet}${fieldHint ? ` Available columns include ${fieldHint}.` : ''} I saved this as the purpose${examples.length ? ' and captured example questions from your reply' : ''}.`;
            break;
        }
        case 'grain': {
            const entities = draft.entities.map((item) => (
                draft.selectedEntityId && item.id !== draft.selectedEntityId
                    ? item
                    : { ...item, grain: answer }
            ));
            draftPatch = { entities };
            assistantMessage = `Grain updated for ${entityName}: ${snippet} Agents should join only on documented keys and not invent extra grain.`;
            break;
        }
        case 'constraints': {
            const constraints = answer
                .split(/\n+/)
                .map((line) => line.replace(/^[-•]\s*/, '').trim())
                .filter(Boolean);
            draftPatch = {
                constraints: constraints.length ? constraints : [answer],
                usageGuidelines: draft.usageGuidelines.length ? draft.usageGuidelines : SYNTHETIC_USAGE_GUIDELINES,
            };
            assistantMessage = `I will enforce these constraints on ${entityName}:\n${(constraints.length ? constraints : [answer]).map((c) => `• ${c}`).join('\n')}\n\nOut-of-scope questions should be refused rather than guessed.`;
            break;
        }
        default: {
            assistantMessage = `Captured for ${entityName}: ${snippet}${fieldHint ? ` I will keep this alongside columns ${fieldHint}.` : ''}`;
            break;
        }
    }

    logContextCopilot('app', 'clarify_reply_generated', {
        questionId: question.id,
        entityId: draft.selectedEntityId,
        answerChars: answer.length,
    });

    return { assistantMessage, draftPatch };
}

export function replyToFollowUp(
    userText: string,
    summary: ParsedMetadataSummary,
    draft: StructuredModelContext
): SyntheticReply {
    const trimmed = userText.trim();
    const entity = summary.entities.find((e) => e.id === draft.selectedEntityId) ?? summary.entities[0];
    const entityName = entity?.displayName || 'the selected entity';
    const intent = detectIntent(trimmed);
    const synthetic = answerQuestion(trimmed, summary, draft);

    if (intent === 'purpose' && draft.purpose.trim()) {
        return {
            intent,
            assistantMessage: `Purpose is already set from your earlier answer. For ${entityName}: ${draft.purpose}\n\nIf you want to change it, say the new purpose explicitly.`,
            draftPatch: {},
        };
    }
    if ((intent === 'constraints') && draft.constraints.length) {
        return {
            intent,
            assistantMessage: `Current constraints for ${entityName}:\n${draft.constraints.map((c) => `• ${c}`).join('\n')}\n\nI can add more if you list them.`,
            draftPatch: {},
        };
    }
    if (intent === 'fallback') {
        const fields = entity?.fields.slice(0, 8).map((f) => `${f.name} (${f.dataType})`).join(', ') || 'no parsed fields';
        return {
            intent,
            assistantMessage: `For ${entityName}: ${draft.analysisSummary || 'metadata is loaded.'}\n\nYou asked: ${trimmed}\n\nColumns I can ground on: ${fields}. ${draft.purpose ? `Purpose: ${draft.purpose}` : 'Add purpose in the questions above if this should become part of the contract.'}`,
            draftPatch: {},
        };
    }

    logContextCopilot('app', 'followup_replied', { intent, chars: trimmed.length, entityId: draft.selectedEntityId });
    return synthetic;
}

export function suggestedQuestions(summary: ParsedMetadataSummary): string[] {
    const primary = summary.entities[0]?.displayName;
    const secondary = summary.entities[1]?.displayName;
    const questions: string[] = [...SUGGESTED_QUESTION_TEMPLATES];
    if (primary && secondary) {
        questions[1] = `What is the grain of ${primary}, and how should it join to ${secondary}?`;
    } else if (primary) {
        questions[1] = `What is the grain of ${primary}?`;
    }
    return questions;
}

export function coverageFromDraft(draft: StructuredModelContext): CoverageState {
    return {
        purpose: Boolean(draft.purpose.trim()),
        domain: Boolean(draft.businessDomain.trim()),
        relationships: draft.relationships.length > 0,
        constraints: draft.constraints.length > 0,
        examples: draft.exampleQuestions.length > 0,
    };
}

function detectIntent(text: string): ModelContextCopilotIntent {
    const q = text.toLowerCase();
    if (/\b(never|must not|don't|dont|constraint|pii|secret|mutate)\b/.test(q)) return 'constraints';
    if (/\b(example|sample|question this|should answer|tool question)\b/.test(q)) return 'examples';
    if (/\b(term|glossary|mean|define|definition|domain)\b/.test(q) && /\b(domain|term|glossary|mean)\b/.test(q)) {
        if (/\bdomain\b/.test(q)) return 'domain';
        return 'glossary';
    }
    if (/\bdomain\b/.test(q)) return 'domain';
    if (/\b(join|relat|foreign|fk|grain)\b/.test(q)) return q.includes('grain') ? 'grain' : 'relationships';
    if (/\b(used for|purpose|who should|what is this model)\b/.test(q)) return 'purpose';
    if (/\bgrain\b/.test(q)) return 'grain';
    return 'fallback';
}

function mergeDraft(base: StructuredModelContext, patch: Partial<StructuredModelContext>): StructuredModelContext {
    return {
        ...base,
        ...patch,
        metadataSummary: patch.metadataSummary ?? base.metadataSummary,
        intendedUse: patch.intendedUse ?? base.intendedUse,
        entities: patch.entities ?? base.entities,
        relationships: patch.relationships ?? base.relationships,
        usageGuidelines: patch.usageGuidelines ?? base.usageGuidelines,
        constraints: patch.constraints ?? base.constraints,
        glossary: patch.glossary ?? base.glossary,
        exampleQuestions: patch.exampleQuestions ?? base.exampleQuestions,
        clarifyingQuestions: patch.clarifyingQuestions ?? base.clarifyingQuestions,
        followUpMessages: patch.followUpMessages ?? base.followUpMessages,
    };
}

export function answerQuestion(
    userText: string,
    summary: ParsedMetadataSummary,
    draft: StructuredModelContext
): SyntheticReply {
    const intent = detectIntent(userText);
    const entityList = summary.entities.map((e) => e.displayName).join(', ') || 'the attached metadata';
    let patch: Partial<StructuredModelContext> = {};
    let assistantMessage = '';

    switch (intent) {
        case 'purpose': {
            const purpose = defaultPurpose(draft.modelName, summary);
            patch = { purpose, intendedUse: SYNTHETIC_INTENDED_USE };
            assistantMessage = `${purpose}\n\nAgents should treat this as a read-oriented tool over ${entityList}. I captured purpose and intended use in the draft.`;
            break;
        }
        case 'domain': {
            const businessDomain = defaultBusinessDomain(summary, draft.sourceType);
            patch = { businessDomain, glossary: defaultGlossary(summary) };
            assistantMessage = `Domain looks like **${businessDomain}** based on ${entityList}. I added a starter glossary from the ingested entities.`;
            break;
        }
        case 'glossary': {
            const glossary = defaultGlossary(summary);
            patch = { glossary, businessDomain: draft.businessDomain || defaultBusinessDomain(summary, draft.sourceType) };
            assistantMessage = `Here are the working definitions:\n${glossary.map((g) => `• ${g.term}: ${g.definition}`).join('\n')}`;
            break;
        }
        case 'relationships':
        case 'grain': {
            const entities = draft.entities.length ? draft.entities : toStructuredEntities(summary);
            const relationships = inferRelationships(summary.entities);
            patch = { entities, relationships };
            const grainLines = entities.slice(0, 4).map((e) => `• ${e.displayName}: ${e.grain}`).join('\n');
            const relLines = relationships.length
                ? relationships.map((r) => `• ${r.from} ${r.type} ${r.to}`).join('\n')
                : '• No confirmed FKs in metadata; keep joins conservative.';
            assistantMessage = `Grain from metadata:\n${grainLines}\n\nJoins:\n${relLines}\n\nI updated the structured draft so later tools can reuse this.`;
            break;
        }
        case 'constraints': {
            patch = { constraints: SYNTHETIC_CONSTRAINTS, usageGuidelines: SYNTHETIC_USAGE_GUIDELINES };
            assistantMessage = `Agents should follow these constraints:\n${SYNTHETIC_CONSTRAINTS.map((c) => `• ${c}`).join('\n')}\n\nI also added usage guidelines for filtering, keys, and refusing out-of-scope questions.`;
            break;
        }
        case 'examples': {
            const exampleQuestions = defaultExampleQuestions(summary);
            patch = { exampleQuestions };
            assistantMessage = `Good tool questions for this model:\n${exampleQuestions.map((q) => `• ${q}`).join('\n')}`;
            break;
        }
        default: {
            assistantMessage = summary.parseOk
                ? `I ingested ${summary.entityCount} entit${summary.entityCount === 1 ? 'y' : 'ies'} and ${summary.fieldCount} fields (${entityList}). Ask about purpose, grain/joins, constraints, or example questions so I can structure context for later LLM tool use.`
                : 'Metadata did not parse cleanly. You can still describe purpose, constraints, and example questions; I will fill remaining slots from defaults when you finalize.';
            break;
        }
    }

    logContextCopilot('app', 'question_answered', {
        intent,
        entityCount: summary.entityCount,
        userChars: userText.length,
    });

    return { intent, assistantMessage, draftPatch: patch };
}

export function applyReply(draft: StructuredModelContext, reply: SyntheticReply): StructuredModelContext {
    return mergeDraft(draft, reply.draftPatch);
}

export function finalizeContext(
    draft: StructuredModelContext,
    summary: ParsedMetadataSummary
): StructuredModelContext {
    const withAnswers = draft.clarifyingQuestions?.length
        ? applyQuestionAnswers(draft, draft.clarifyingQuestions)
        : draft;
    const scopedEntities = withAnswers.selectedEntityId
        ? summary.entities.filter((e) => e.id === withAnswers.selectedEntityId)
        : summary.entities;
    const scopedSummary: ParsedMetadataSummary = {
        ...summary,
        entities: scopedEntities.length ? scopedEntities : summary.entities,
        entityCount: (scopedEntities.length ? scopedEntities : summary.entities).length,
        fieldCount: (scopedEntities.length ? scopedEntities : summary.entities).reduce((sum, e) => sum + e.fields.length, 0),
    };
    const filled: StructuredModelContext = {
        ...withAnswers,
        generatedAt: new Date().toISOString(),
        status: 'finalized',
        purpose: withAnswers.purpose.trim()
            || withAnswers.contextNarrative?.trim()
            || withAnswers.analysisSummary?.trim()
            || defaultPurpose(withAnswers.modelName, scopedSummary),
        businessDomain: withAnswers.businessDomain.trim() || defaultBusinessDomain(scopedSummary, withAnswers.sourceType),
        intendedUse: withAnswers.intendedUse.length ? withAnswers.intendedUse : SYNTHETIC_INTENDED_USE,
        entities: withAnswers.entities.length ? withAnswers.entities : toStructuredEntities(scopedSummary),
        relationships: withAnswers.relationships.length ? withAnswers.relationships : inferRelationships(scopedSummary.entities),
        usageGuidelines: withAnswers.usageGuidelines.length ? withAnswers.usageGuidelines : SYNTHETIC_USAGE_GUIDELINES,
        constraints: withAnswers.constraints.length ? withAnswers.constraints : SYNTHETIC_CONSTRAINTS,
        glossary: withAnswers.glossary.length ? withAnswers.glossary : defaultGlossary(scopedSummary),
        exampleQuestions: withAnswers.exampleQuestions.length ? withAnswers.exampleQuestions : defaultExampleQuestions(scopedSummary),
        llmInstructions: '',
        selectedEntityId: withAnswers.selectedEntityId,
        clarifyingQuestions: withAnswers.clarifyingQuestions,
        followUpMessages: withAnswers.followUpMessages,
        sourceMetadata: withAnswers.sourceMetadata,
        sourceKind: withAnswers.sourceKind,
        usageNotes: withAnswers.usageNotes ?? [],
        elementDescriptions: withAnswers.elementDescriptions ?? [],
        contextNarrative: withAnswers.contextNarrative || withAnswers.analysisSummary || '',
        analysisSummary: withAnswers.contextNarrative || withAnswers.analysisSummary,
    };
    filled.llmInstructions = packLlmInstructions(filled);

    logContextCopilot('app', 'context_finalized', {
        entityCount: filled.metadataSummary.entityCount,
        fieldCount: filled.metadataSummary.fieldCount,
        constraintCount: filled.constraints.length,
        exampleCount: filled.exampleQuestions.length,
        llmInstructionChars: filled.llmInstructions.length,
    });

    return filled;
}

export function newMessageId(): string {
    return `mctx-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
